import { t } from '@app/i18n';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  claimsErrorMessage,
  methodOfTheTarget,
  parseClaims,
  type RpcMethod,
  type SimulatorFormValues,
  type TargetMode,
} from './SimulatorModel';

/**
 * Validação local de T18. Só o que a tela sabe: identidade escolhida, o alvo do
 * modo em uso (tool **ou** agente, nunca os dois — `toolOrAgentNotBoth`) e — no
 * modo Claims — um JSON que o contrato aceite. O resto é do PDP.
 *
 * O token **não** é validado — nem formato de JWT, nem presença. Sem token o PDP
 * nega por falta de sujeito, e isso é um resultado que o operador quer ver; um
 * "informe um JWT válido" aqui esconderia justamente a resposta que ele veio
 * buscar. Token malformado também é resposta do PDP, não do formulário.
 *
 * As CLAIMS, ao contrário, se validam aqui — e a diferença não é incoerência.
 * Um token que o PDP recusa é uma RESPOSTA («este token não passa»); um JSON com
 * vírgula sobrando não chega a ser uma pergunta. A régua é a mesma do resto da
 * tela: barra-se o que não tem como virar pergunta, e deixa-se passar tudo que o
 * PDP saiba responder — inclusive «não».
 *
 * O que se recusa é o que o contrato recusa (`claimValueShapeInvalid`,
 * `claimsMustBeObject`, `claimNameRequired`), não uma régua própria: dois
 * critérios diferentes para a mesma coisa dariam um formulário que aceita o que o
 * backend nega, ou pior, que nega o que o backend aceita.
 *
 * **Função, e não const de módulo.** O schema carrega as mensagens de erro, e uma
 * const seria avaliada no import: as frases congelariam no idioma do primeiro
 * render enquanto o resto da tela trocaria. Mesmo formato de `identitySchema()`.
 */
export function simulatorSchema(): yup.ObjectSchema<SimulatorFormValues> {
  return yup.object({
    identityId: yup.string().required(t('simulator.validation.identityRequired')),
    targetMode: yup.mixed<SimulatorFormValues['targetMode']>().oneOf(['tool', 'agent']).required(),
    // Exigida **só no modo Tool**: o texto que ficou aqui depois de trocar para
    // A2A não viaja no corpo, então não pode bloquear o **Avaliar** — a mesma
    // regra que já valia entre JWT e Claims.
    tool: yup
      .string()
      .defined()
      .trim()
      .max(200, t('simulator.validation.toolTooLong'))
      .when('targetMode', {
        is: 'tool',
        then: (schema) => schema.required(t('simulator.validation.toolRequired')),
      }),
    agent: yup
      .string()
      .defined()
      .trim()
      .max(200, t('simulator.validation.agentTooLong'))
      .when('targetMode', {
        is: 'agent',
        then: (schema) => schema.required(t('simulator.validation.agentRequired')),
      }),
    rpcMethod: yup
      .mixed<SimulatorFormValues['rpcMethod']>()
      .required()
      .test('method-of-the-target', t('simulator.validation.methodOfTheTarget'), (method, context) =>
        Boolean(method) && methodOfTheTarget(method as RpcMethod, context.parent.targetMode as TargetMode),
      ),
    subjectMode: yup
      .mixed<SimulatorFormValues['subjectMode']>()
      .oneOf(['token', 'claims'])
      .required(),
    token: yup.string().defined(),
    claims: yup
      .string()
      .defined()
      // Só no modo Claims: o texto que ficou na textarea depois de voltar ao modo
      // JWT não viaja no corpo, então não pode bloquear o **Avaliar**.
      .test('claims', function (value) {
        if (this.parent.subjectMode !== 'claims') return true;
        const parsed = parseClaims(value ?? '');
        return parsed.ok || this.createError({ message: claimsErrorMessage(parsed.problem) });
      }),
  });
}

export function useSimulatorValidation() {
  return {
    resolver: yupResolver(simulatorSchema()),
    // `onChange`: **Avaliar** habilita quando identidade e alvo estão de pé.
    mode: 'onChange' as const,
  };
}
